import React from 'react';
import {
  Modal,
  ModalSection,
  Gutter,
  Form,
  Dropdown,
  DateInput,
  Grid,
  GridRow,
  GridColumn,
  ParagraphHeader,
  Well,
  Location,
} from '@axiom/ui';
import {
  billingUnitValues,
  estimatedDurationValues,
  worksiteValues,
  rawDateInputDirections,
  PositionsConst,
  ProjectNeedsDefaultRemoteLocation,
} from '@axiom/const';

import type { InterviewCandidate } from '../../types/interview-candidate.types';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';
import { TalentInterviewModalFooter } from '../TalentInterviewModalFooter/TalentInterviewModalFooter';
import {
  TalentInterviewMiniIntakeModalSchema,
  type TalentInterviewMiniIntakeForm,
} from '../../schema/talent-interview-mini-intake-modal-schema';

export interface TalentInterviewMiniIntakeModalProps {
  candidate: InterviewCandidate;
  name?: string;
  onExitInterviewFlow: () => void;
  onContinue: (data: TalentInterviewMiniIntakeForm) => void | Promise<void>;
  onBack?: () => void;
}

export const TalentInterviewMiniIntakeModal = ({
  candidate,
  name = 'MINI_INTAKE_MODAL',
  onExitInterviewFlow,
  onContinue,
  onBack,
}: TalentInterviewMiniIntakeModalProps) => {
  const initialValues = {
    billingUnitsPerWeek: undefined,
    startDate: undefined,
    estimatedDuration: undefined,
    worksite: undefined,
    companyLocation: ProjectNeedsDefaultRemoteLocation,
    primaryFocusArea: candidate.primaryFocusArea ?? '',
  };

  return (
    <Form
      name="MINI_INTAKE_FORM"
      schema={TalentInterviewMiniIntakeModalSchema}
      initialValues={initialValues}
      onSubmit={async (_, __, values) => {
        await onContinue(values);
      }}
    >
      {({ fireSubmit, values, setValues }) => {
        const showLocationField =
          values.worksite !== PositionsConst.Worksites.Remote &&
          !!values.worksite;

        return (
          <Modal name={name}>
            <TalentInterviewModalHeader
              candidate={candidate}
              name="MINI_INTAKE_MODAL_HEADER"
              onClose={onExitInterviewFlow}
            />
            <ModalSection>
              <ParagraphHeader name="SECTION_TITLE">
                When and where will you need{' '}
                {candidate.calculatedFirstName ||
                  candidate.calculatedDisplayName}{' '}
                to work?
              </ParagraphHeader>
              <Gutter bottom="24px" />
              <Grid>
                <GridRow gutterBottom="24px">
                  <GridColumn
                    largeScreenWidth={6}
                    smallScreenWidth={12}
                    tabletWidth={6}
                  >
                    <Dropdown
                      name="billingUnitsPerWeek"
                      label="Estimated hours per week (5 - 40)"
                      options={billingUnitValues.map(value => ({
                        label: String(value),
                        value,
                      }))}
                      displayKey="label"
                      valueKey="value"
                      disableArrowKeyChange
                    />
                    <Gutter only="mobile" bottom="24px" />
                  </GridColumn>
                  <GridColumn
                    largeScreenWidth={4}
                    smallScreenWidth={12}
                    tabletWidth={5}
                  >
                    <DateInput
                      name="startDate"
                      minDate={new Date()}
                      label="Estimated start date"
                      direction={rawDateInputDirections.Down}
                    />
                  </GridColumn>
                </GridRow>
                <GridRow gutterBottom="24px">
                  <GridColumn
                    largeScreenWidth={4}
                    smallScreenWidth={12}
                    tabletWidth={5}
                  >
                    <Dropdown
                      name="estimatedDuration"
                      label="Estimated duration"
                      options={Object.keys(estimatedDurationValues).map(
                        (key, index) => ({
                          label: Object.values(estimatedDurationValues)[index],
                          value: key,
                        })
                      )}
                      displayKey="label"
                      valueKey="value"
                      disableArrowKeyChange
                    />
                  </GridColumn>
                </GridRow>
                <GridRow>
                  <GridColumn width={12}>
                    <Well background="light blue">
                      <Grid>
                        <GridColumn
                          smallScreenWidth={12}
                          tabletWidth={7}
                          largeScreenWidth={7}
                        >
                          <Dropdown
                            name="worksite"
                            label="Do you need the work done in your office?"
                            options={Object.keys(worksiteValues).map(
                              (key, index) => ({
                                label: Object.values(worksiteValues)[index],
                                value: key,
                              })
                            )}
                            displayKey="label"
                            valueKey="value"
                            onChange={async value => {
                              if (value === PositionsConst.Worksites.Remote) {
                                await setValues({
                                  ...values,
                                  worksite: value,
                                  addressCountryCode: 'US',
                                  companyLocation:
                                    ProjectNeedsDefaultRemoteLocation,
                                });
                              } else if (
                                values.companyLocation?.locationName ===
                                ProjectNeedsDefaultRemoteLocation.locationName
                              ) {
                                await setValues({
                                  ...values,
                                  worksite:
                                    value as TalentInterviewMiniIntakeForm['worksite'],
                                  addressCountryCode: 'US',
                                  companyLocation: undefined,
                                });
                              }
                            }}
                            disableArrowKeyChange
                          />
                          <Gutter only="mobile" bottom="24px" />
                        </GridColumn>
                        {showLocationField ? (
                          <GridColumn
                            smallScreenWidth={12}
                            tabletWidth={5}
                            largeScreenWidth={5}
                          >
                            <Location
                              name="companyLocation"
                              label="Office street address (US)"
                            />
                          </GridColumn>
                        ) : null}
                      </Grid>
                    </Well>
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <TalentInterviewModalFooter
              name="MINI_INTAKE_MODAL_FOOTER"
              onClose={onExitInterviewFlow}
              onBack={onBack}
              onContinue={fireSubmit}
            />
          </Modal>
        );
      }}
    </Form>
  );
};
